//Objects positions are described in View Space, pre-Viewport Transform performed only on .x

import { GStack, GetVec3, Matrix4x4, Vector3 } from "../core/types";
import { Vec3Negate, MathVector3Normalize, ComputeLookToMatrix, GTime, Vec3Dot } from "../core/utils";
import { GScreenDesc } from "../core/screenDesc";
import { GUI } from "dat.gui";
import { TransformFromNDCToWorld } from "../helpers/transform";
import { DatGuiAddVec3 } from "../core/gui";
import { GUserInputDesc } from "../core/input";
import { SceneUniformBufferParameters, SubmitSceneUniformBufferParamsDebugUI } from "./sceneUniformBufferParams";
import { RLightprobesManager } from "../lightProbes";
import { Mesh } from "../basePass";
import { RTexture } from "../core/texture";
import { Shader } from "../core/shaderUtils";

//TODO DEPRECATED, LEAVE AS IS
export const GSceneDesc = {
    //GPU-side Cam is packed like vec3(Offset.xyz, Zoom)
    Camera: {
        Position: { x: 0.0, y: 0.0, z: -4.0 },
        ZoomScale: 2.0,
        OrientationEuler: { yaw: 0.0, pitch: 0.0 },
    },
    //Surface to be burned
    FirePlane: {
        PositionOffset: GetVec3(0.0, 0.0, 0.0),
        SizeScale: 1.0,
        OrientationEuler: { yaw: 0.0, pitch: 0.0, roll: 0.0 },
    },
    Floor: {
        Position: { x: 0.0, y: -1, z: 0.0 },
        SizeScale: 4.0,
    },

    //GPU Side SpotlightDesc: vec3(Radius, ConeAngles.xy)
    Spotlight: {
        Position: GetVec3(0.0, 2.5, -1.0),
        FocusPosition: { x: 0.0, y: 0.0, z: 1.5 }, //To deduce light direction
        SizeScale: { x: 1.5, y: 2.5 },
        ProjectedLightSizeScale: { x: 1.0, y: 1.0 },
        Radius: 7,
        ConeAngles: { x: 0.1, y: 0.4 }, //Radians, Cone Inner, Cone Outer, Inner < Outer,
        GetDirection: function () {
            const sp = GSceneDesc.Spotlight.FocusPosition;
            return MathVector3Normalize(Vec3Negate(GetVec3(sp.x, -1.0 + sp.y, sp.z), GSceneDesc.Spotlight.Position));
        },
    },

    Tool: {
        Position: { x: 0.0, y: 0.0, z: -1.0 }, //World Space
        Radius: 0,
        Color: { r: 0.0, g: 0.0, b: 0.0 },
    },
};

export function GSceneDescSubmitDebugUI(datGui: dat.GUI) {
    {
        const folder = datGui.addFolder("SceneDesc");
        //folder.open();
        folder.add(GSceneDesc.FirePlane.PositionOffset, "x", -2, 5).name("PlanePosX").step(0.01).listen();
        folder.add(GSceneDesc.FirePlane.PositionOffset, "y", -3, 10).name("PlanePosY").step(0.01).listen();
        folder.add(GSceneDesc.FirePlane.PositionOffset, "z", -10, 2).name("PlanePosZ").step(0.01).listen();
        folder.add(GSceneDesc.FirePlane.OrientationEuler, "pitch", -Math.PI, Math.PI).name("pitch").step(0.01);
        folder.add(GSceneDesc.FirePlane.OrientationEuler, "yaw", -Math.PI, Math.PI).name("yaw").step(0.01);
        folder.add(GSceneDesc.FirePlane.OrientationEuler, "roll", -Math.PI, Math.PI).name("roll").step(0.01);

        folder.add(GSceneDesc.Camera.Position, "x", -5, 5).name("CamPosX").step(0.01).listen();
        folder.add(GSceneDesc.Camera.Position, "y", -3, 10).name("CamPosY").step(0.01).listen();
        folder.add(GSceneDesc.Camera.Position, "z", -10, 2).name("CamPosZ").step(0.01).listen();
        folder.add(GSceneDesc.Camera, "ZoomScale", 0, 5).name("Zoom").step(0.01).listen();

        //folder.add(GSceneDesc.Floor.PositionOffset, "x", -2, 5).name("FloorPosX").step(0.01);
        folder.add(GSceneDesc.Floor.Position, "y", -3, 10).name("FloorPosY").step(0.01).listen();
        //folder.add(GSceneDesc.Floor.PositionOffset, "z", -10, 2).name("FloorPosZ").step(0.01);

        folder.add(GSceneDesc.Floor, "SizeScale", -2, 5).name("FloorSizeScale").step(0.01);

        const spotlightFolder = folder.addFolder("Spotlight");
        //spotlightFolder.open();
        spotlightFolder.add(GSceneDesc.Spotlight.SizeScale, "x", -2, 5).name("LightSizeX").step(0.01);
        spotlightFolder.add(GSceneDesc.Spotlight.SizeScale, "y", -2, 5).name("LightSizeY").step(0.01).listen();
        spotlightFolder.add(GSceneDesc.Spotlight.Position, "x", -10, 10).name("LightPosX").step(0.01).listen();
        spotlightFolder.add(GSceneDesc.Spotlight.Position, "y", -10, 10).name("LightPosY").step(0.01).listen();
        spotlightFolder.add(GSceneDesc.Spotlight.Position, "z", -10, 10).name("LightPosZ").step(0.01).listen();
        spotlightFolder.add(GSceneDesc.Spotlight.FocusPosition, "x", -3, 10).name("FocusPosX").step(0.01).listen();
        spotlightFolder.add(GSceneDesc.Spotlight.FocusPosition, "y", -3, 10).name("FocusPosY").step(0.01).listen();
        spotlightFolder.add(GSceneDesc.Spotlight.FocusPosition, "z", -3, 10).name("FocusPosZ").step(0.01).listen();
        spotlightFolder.add(GSceneDesc.Spotlight, "Radius", 0, 10).name("Radius").step(0.01);
        spotlightFolder
            .add(GSceneDesc.Spotlight.ConeAngles, "x", 0, Math.PI / 2)
            .name("InnerAngle")
            .step(0.01);
        spotlightFolder
            .add(GSceneDesc.Spotlight.ConeAngles, "y", 0, Math.PI / 2)
            .name("OuterAngle")
            .step(0.01);

        spotlightFolder.add(GSceneDesc.Spotlight.ProjectedLightSizeScale, "x", 0, 40).name("ProjSizeX").step(0.01);
        spotlightFolder.add(GSceneDesc.Spotlight.ProjectedLightSizeScale, "y", 0, 40).name("ProjSizeY").step(0.01);
    }
}
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

export class SceneUniformBufferCPU {
    Params = new SceneUniformBufferParameters();

    //private:
    BufferGPU: WebGLBuffer | null = null;
    private ParamsArr = this.Params.GenerateMembersArr();
    private SerializedBuffer;

    constructor() {
        let totalSize = 0;
        for (const member of this.ParamsArr) {
            totalSize += member.Data.length;
        }
        this.SerializedBuffer = new Float32Array(totalSize);
    }

    UploadParamsToSerializedBuffer() {
        let offset = 0;
        for (const member of this.ParamsArr) {
            this.SerializedBuffer.set(member.Data, offset);
            offset += member.Data.length;
        }
    }

    GetBufferSerizalized() {
        return this.SerializedBuffer;
    }
}

export class SceneBase {
    static UniformBuffer = new SceneUniformBufferCPU();

    //All Scene Meshes to be rendered
    AllMeshesArr: Mesh[] = [];

    static GpCurProjectorTexture: WebGLTexture | null;

    //Can be either a cubemap or rect texture
    static TextureEnvMapSize = 512; //Size of each cube face
    static TextureEnvMap: WebGLTexture | null = null;
    static TextureEnvMapWithMips: WebGLTexture | null = null;
    static TextureEnvMapFBO: WebGLFramebuffer[] | null = null;
    static TextureEnvMapRect = new RTexture();
    static bEnvMapProcessed = false;

    static LightprobesManager = new RLightprobesManager();

    //Pointer Light
    static LightFollowPointer = true;

    //Base Pass Render Program (depends on Scene SDF)
    ShaderBasePassCurScene: Shader | null = null;

    static PointerLightDepth = 14.5; //-CamPos

    bSceneFinished = false;
    bCustomEventFlag = 0; //0 Disabled - 1 Enabled - 2 Processed

    OnUpdate(gl: WebGL2RenderingContext) {
        if (SceneBase.LightFollowPointer) {
            const posWS = GStack.TempVec3;
            TransformFromNDCToWorld(GUserInputDesc.InputPosCurNDC, SceneBase.PointerLightDepth, posWS);
            SceneBase.UniformBuffer.Params.SpotlightTargetPos.Set(posWS);
        }
        this.ComputeProjectorViewMatrix();
        SceneBase.UpdateUniformBufferParamsFromExternal();
        SceneBase.ComputeAndGetCameraViewMatrix();
        SceneBase.UploadSerializedUniformBufferToGPU(gl);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Render(gl: WebGL2RenderingContext) {}

    static InitUniformBuffer(gl: WebGL2RenderingContext) {
        SceneBase.UniformBuffer.BufferGPU = gl.createBuffer()!;
        this.UploadSerializedUniformBufferToGPU(gl);
    }

    static UploadSerializedUniformBufferToGPU(gl: WebGL2RenderingContext) {
        gl.bindBuffer(gl.UNIFORM_BUFFER, SceneBase.UniformBuffer.BufferGPU);
        SceneBase.UniformBuffer.UploadParamsToSerializedBuffer();
        const bufferCPU = SceneBase.UniformBuffer.GetBufferSerizalized();
        gl.bufferData(gl.UNIFORM_BUFFER, bufferCPU, gl.DYNAMIC_DRAW);
    }

    static UpdateUniformBufferParamsFromExternal() {
        SceneBase.UniformBuffer.Params.CameraPosition.Set3(
            GSceneDesc.Camera.Position.x,
            GSceneDesc.Camera.Position.y,
            GSceneDesc.Camera.Position.z,
        );
        SceneBase.UniformBuffer.Params.CameraZoom.x = GSceneDesc.Camera.ZoomScale;
        SceneBase.UniformBuffer.Params.ScreenRatio.x = GScreenDesc.ScreenRatio;

        SceneBase.UniformBuffer.Params.Time.x = GTime.CurClamped;
        SceneBase.UniformBuffer.Params.DeltaTime.x = GTime.Delta * GTime.DeltaTimeScale;

        SceneBase.UniformBuffer.Params.UserInputPosNDC.Set3(
            GUserInputDesc.InputPosCurNDC.x,
            GUserInputDesc.InputPosCurNDC.y,
            0.0,
        );
    }

    //Projector
    WorldToProjectorSpaceMatCPU = new Matrix4x4();
    ComputeProjectorViewMatrix() {
        const projDir = GStack.TempVec3;
        projDir.Set(SceneBase.UniformBuffer.Params.ProjectorTargetPos);
        projDir.Negate(SceneBase.UniformBuffer.Params.ProjectorPos);

        //projDir.Set3(1.0, 0.0, 1.0);

        projDir.Normalize();
        const up = GStack.TempVec3_2;
        up.Set3(0, 1, 0);
        ComputeLookToMatrix(SceneBase.UniformBuffer.Params.ProjectorPos, projDir, up, this.WorldToProjectorSpaceMatCPU);
        SceneBase.UniformBuffer.Params.WorldToProjectorSpaceMat.Set(this.WorldToProjectorSpaceMatCPU);
    }

    //Camera
    static CameraDirection = new Vector3(0.0, 0.0, 1.0);
    static WorldToCameraSpaceMatCPU = new Matrix4x4();
    static ComputeAndGetCameraViewMatrix() {
        this.CameraDirection.Normalize();
        const camDir = this.CameraDirection;
        const up = GStack.TempVec3;
        up.Set3(0, 1, 0);
        if (Math.abs(/* Math.abs */ Vec3Dot(camDir, up) - 1.0) < 0.1) {
            //camera looks up
            up.Set3(0, 0, -1);
        }
        const down = GStack.TempVec3_2;
        down.Set3(0, -1, 0);
        if (Math.abs(Vec3Dot(camDir, down) - 1.0) < 0.1) {
            //camera looks down
            up.Set3(0, 0, 1);
        }
        ComputeLookToMatrix(
            SceneBase.UniformBuffer.Params.CameraPosition,
            this.CameraDirection,
            up,
            SceneBase.WorldToCameraSpaceMatCPU,
        );
        return SceneBase.WorldToCameraSpaceMatCPU.serializeForGPUImport();
    }
    static CameraLookRight() {
        this.CameraDirection.Set3(1.0, 0.0, 0.0);
    }
    static CameraLookLeft() {
        this.CameraDirection.Set3(-1.0, 0.0, 0.0);
    }
    static CameraLookUp() {
        this.CameraDirection.Set3(0.0, -1.0, 0.0);
    }
    static CameraLookDown() {
        this.CameraDirection.Set3(0.0, 1.0, 0.0);
    }
    static CameraLookForward() {
        this.CameraDirection.Set3(0.0, 0.0, 1.0);
    }
    static CameraLookBackward() {
        this.CameraDirection.Set3(0.0, 0.0, -1.0);
    }
    static CubemapFaceLookAtMap: { [index: number]: () => void } = {
        0: () => this.CameraLookRight(),
        1: () => this.CameraLookLeft(),
        2: () => this.CameraLookUp(),
        3: () => this.CameraLookDown(),
        4: () => this.CameraLookForward(),
        5: () => this.CameraLookBackward(),
    };

    static PaintingColorHex = "#ffffff";
    static ProjectorColorHex = "#ffffff";
    static LightColorHex = "#ffffff";

    SubmitDebugUI(datGui: dat.GUI): GUI {
        const folder = datGui.addFolder("Scene");

        folder.open();

        DatGuiAddVec3(
            folder,
            SceneBase.UniformBuffer.Params.OccluderPlaneCenter,
            `OccluderPlaneCenter`,
            -20,
            20,
            true,
            true,
        );
        DatGuiAddVec3(
            folder,
            SceneBase.UniformBuffer.Params.OccluderPlaneExtent,
            `OccluderPlaneExtent`,
            -20,
            20,
            true,
            true,
        );

        const camFolder = folder.addFolder("Camera");

        camFolder.add(GSceneDesc.Camera.Position, "x", -5, 5).name("CamPosX").step(0.001).listen();
        camFolder.add(GSceneDesc.Camera.Position, "y", -3, 10).name("CamPosY").step(0.001).listen();
        camFolder.add(GSceneDesc.Camera.Position, "z", -10, 2).name("CamPosZ").step(0.001).listen();
        camFolder.add(GSceneDesc.Camera, "ZoomScale", 0, 5).name("Zoom").step(0.01).listen();

        DatGuiAddVec3(camFolder, SceneBase.CameraDirection, `CamDir`, -1, 1, true);

        /* const CameraRotator = {
            LookLeft: function () {
                this.LookLeft();
            },
        }; */

        // Add a button to the GUI
        camFolder.add(SceneBase, "CameraLookLeft");
        camFolder.add(SceneBase, "CameraLookRight");

        SubmitSceneUniformBufferParamsDebugUI(folder);

        SceneBase.LightprobesManager.SubmitDebugUI(folder);

        const mainMeshes = folder.addFolder("Meshes");
        let c = 0;
        for (const meshRef of this.AllMeshesArr) {
            const name = meshRef.Name !== null ? meshRef.Name : c.toString();
            const meshFolder = mainMeshes.addFolder(name);
            DatGuiAddVec3(meshFolder, meshRef.Position, `Position`, -10, 10, false);
            DatGuiAddVec3(meshFolder, meshRef.Scale, `Scale`, 0.01, 20, false);
            DatGuiAddVec3(meshFolder, meshRef.Orientation, `Rotation`, -Math.PI, Math.PI, false);
            meshFolder.add(meshRef.Material, "Metalness", 0, 1).name("Metalness").step(0.01);
            meshFolder.add(meshRef.Material, "Brightness", 0, 5).name("Brightness").step(0.01).listen();
            meshFolder.add(meshRef.Material, "TextureScale", 0, 5).name("UVScale").step(0.01);
            c++;
        }

        return folder;
    }

    // Use it to redefine scene extents based on device view ratio
    /* ComputeWorldBounds() {
        //Based on Cur Cam Pos and Zoom
        //left
        const ndcStartWS = GStack.TempVec3;
        TransformFromNDCToWorld(GetVec2(-1, -1), -GSceneDesc.Camera.Position.z, ndcStartWS);

        const ndcEndWS = GStack.TempVec3_2;
        TransformFromNDCToWorld(GetVec2(1, 1), -GSceneDesc.Camera.Position.z, ndcEndWS);

        SceneBase.WorldBoundsStart.x = ndcStartWS.x;
        SceneBase.WorldBoundsStart.y = ndcStartWS.y;

        SceneBase.WorldBoundsLength.x = ndcEndWS.x - ndcStartWS.x;
        SceneBase.WorldBoundsLength.y = ndcEndWS.y - ndcStartWS.y;
    } */
}
